import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../Components/Layout';
import PaginatedItems from "../Components/Pagination";

const QuotingHistory = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const searchTimerRef = useRef(null);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
        }, 300);
    };

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    const fetchData = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page: page.toString(),
                limit: '10',
                sort_by: sortBy,
                sort_order: sortOrder
            };
            
            if (search) params.search = search;
            
            const response = await axios.get('/library/quoting-history', { params });
            const result = response.data;
            
            if (result.success) {
                setData(result.data);
                setCurrentPage(result.pagination.current_page);
                setTotalPages(result.pagination.total_pages);
                setTotalRecords(result.pagination.total_records);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
    }, [debouncedSearch, sortBy, sortOrder]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const handlePageChange = (page) => {
        fetchData(page);
    };

    const paginatePage = (page) => {
        console.log(`pageNumber ! ${page}`)
        handlePageChange(page);
    };

    const getTypeBadgeClass = (type) => {
        switch(type?.toLowerCase()) {
            case 'quote': return 'bg-primary';
            case 'order': return 'bg-success';
            case 'rfq': return 'bg-warning';
            default: return 'bg-secondary';
        }
    };

    return (
        <Layout>
            <div style={{ height: 'calc(100vh - 70px)', overflow: 'auto', padding: '20px 0px' }}>
                <div className="container-fluid quoting-history">
            {/* Search and Actions Bar */}
            <div className="row mb-3 p-3 bg-light">
                <div className="col-md-6">
                    <div className="input-group">
                        <span className="input-group-text">
                            <i className="ti-search"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search quotes, items, vendors..."
                            value={search}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>
                <div className="col-md-6 text-end">
                    <small className="text-muted">
                        {totalRecords} quotes found
                    </small>
                </div>
            </div>

            {/* Data Table with Horizontal Scroll */}
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
                {loading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden"></span>
                        </div>
                        <p className="mt-2">Loading quoting history...</p>
                    </div>
                ) : (
                    <table className="table table-hover" style={{ minWidth: '1600px' }}>
                        <thead className="table-light">
                            <tr>
                                <th 
                                    onClick={() => handleSort('id')}
                                    style={{ cursor: 'pointer', minWidth: '60px' }}
                                >
                                    ID {sortBy === 'id' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Active_Status')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Status {sortBy === 'Active_Status' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Type')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Type {sortBy === 'Type' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Item')}
                                    style={{ cursor: 'pointer', minWidth: '150px' }}
                                >
                                    Item {sortBy === 'Item' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th style={{ minWidth: '200px' }}>Description</th>
                                <th style={{ minWidth: '200px' }}>Purchase Description</th>
                                <th style={{ minWidth: '80px' }}>U/M</th>
                                <th 
                                    onClick={() => handleSort('Cost')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Cost {sortBy === 'Cost' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Preferred_Vendor')}
                                    style={{ cursor: 'pointer', minWidth: '150px' }}
                                >
                                    Preferred Vendor {sortBy === 'Preferred_Vendor' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Price')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Price {sortBy === 'Price' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th style={{ minWidth: '100px' }}>Min Order Qty</th>
                                <th style={{ minWidth: '100px' }}>Pack Size</th>
                                <th style={{ minWidth: '100px' }}>Lead Time</th>
                                <th style={{ minWidth: '120px' }}>Tier Qty 1 Price</th>
                                <th style={{ minWidth: '120px' }}>Tier Qty 2 Price</th>
                                <th style={{ minWidth: '120px' }}>Tier Qty 3 Price</th>
                                <th 
                                    onClick={() => handleSort('DateOfUpdate')}
                                    style={{ cursor: 'pointer', minWidth: '120px' }}
                                >
                                    Date of Update {sortBy === 'DateOfUpdate' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{item.Active_Status}</td>
                                    <td>{item.Type}</td>
                                    <td>{item.Item}</td>
                                    <td>{item.Description}</td>
                                    <td>{item.Purchase_Description}</td>
                                    <td>{item.U_M}</td>
                                    <td>{item.Cost}</td>
                                    <td>{item.Preferred_Vendor}</td>
                                    <td>{item.Price}</td>
                                    <td>{item.Min_Order_Qty}</td>
                                    <td>{item.Pack_Size}</td>
                                    <td>{item.Lead_Time}</td>
                                    <td>{item.TierQty1Price}</td>
                                    <td>{item.TierQty2Price}</td>
                                    <td>{item.TierQty3Price}</td>
                                    <td>{item.DateOfUpdate}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination - Using PaginatedItems Component */}
            {!loading && totalRecords > 0 && (
                <div className="col-lg-12 mb-12" style={{marginTop:'20px'}}>
                    <PaginatedItems total={totalRecords} onPageChange={paginatePage} currentPage={currentPage}></PaginatedItems>
                </div>
            )}
                </div>
            </div>
        </Layout>
    );
};

export default QuotingHistory;